import { useMemo, useCallback } from 'react';
import { useFile, useVisualizer, useUI } from '../context/AppContext.jsx';
import {
  createIKSolver,
  computeIKSolution,
  computeAllIKSolutions,
  verifyIKSolution,
  selectBestSolution
} from '../services/IKService.js';

/**
 * Hook for computing inverse kinematics for waypoints
 * Provides functions to calculate joint angles from waypoint poses
 */
export function useIKSolver() {
  const {
    currentWaypoints,
    setCurrentWaypoints,
    currentFeatures,
    parser,
  } = useFile();
  const { visualizer } = useVisualizer();
  const { currentRobotType, showStatus } = useUI();

  // Create IK solver for current robot type using service
  const ikSolver = useMemo(() => {
    const { solver, error } = createIKSolver(currentRobotType);
    if (error) {
      console.error('Failed to create IK solver:', error);
      return null;
    }
    return solver;
  }, [currentRobotType]);

  /**
   * Compute IK for a waypoint and update its joint positions
   * @param {number} waypointIndex - Index of waypoint to update
   * @param {boolean} updateViaPoint - Whether to update via point (if exists)
   * @returns {boolean} Success status
   */
  const computeIKForWaypoint = useCallback(
    async (waypointIndex, updateViaPoint = false) => {
      if (!ikSolver) {
        showStatus('IK solver not available for this robot type', 'error');
        return false;
      }

      const waypoint = currentWaypoints[waypointIndex];
      if (!waypoint) {
        showStatus('Waypoint not found', 'error');
        return false;
      }

      try {
        // Get waypoint target pose (position + rotation)
        const targetPose = {
          position: { ...waypoint.position },
          rotation: { ...waypoint.rotation },
        };

        // Use service to compute IK
        const initialJoints = waypoint.jointPositions || null;
        const { solution, error } = computeIKSolution(ikSolver, targetPose, initialJoints);

        if (error || !solution) {
          showStatus(`No IK solution found for waypoint ${waypoint.number}`, 'error');
          return false;
        }

        // Update waypoint with new joint positions
        const updatedWaypoints = [...currentWaypoints];
        updatedWaypoints[waypointIndex] = {
          ...updatedWaypoints[waypointIndex],
          jointPositions: solution,
        };

        // Update via point if requested and exists
        if (updateViaPoint && waypoint.viaPoint) {
          const viaTargetPose = {
            position: { ...waypoint.viaPoint.position },
            rotation: { ...waypoint.viaPoint.rotation },
          };

          const { solution: viaSolution } = computeIKSolution(ikSolver, viaTargetPose, solution);

          if (viaSolution) {
            updatedWaypoints[waypointIndex].viaPoint = {
              ...updatedWaypoints[waypointIndex].viaPoint,
              jointPositions: viaSolution,
            };
          }
        }

        // Update parser
        parser.updateWaypointJoints(waypointIndex, solution);

        // Update state
        setCurrentWaypoints(updatedWaypoints);

        showStatus(`IK computed for waypoint ${waypoint.number}`, 'success');
        return true;
      } catch (error) {
        console.error('Error computing IK:', error);
        showStatus(`Error computing IK: ${error.message}`, 'error');
        return false;
      }
    },
    [ikSolver, currentWaypoints, setCurrentWaypoints, parser, showStatus]
  );

  /**
   * Compute IK for multiple waypoints
   * @param {Array<number>} waypointIndices - Array of waypoint indices
   * @returns {Object} Result with success count
   */
  const computeIKForMultipleWaypoints = useCallback(
    async (waypointIndices) => {
      if (!ikSolver) {
        showStatus('IK solver not available for this robot type', 'error');
        return { success: 0, failed: waypointIndices.length };
      }

      let successCount = 0;
      let failedCount = 0;

      for (const index of waypointIndices) {
        const success = await computeIKForWaypoint(index, false);
        if (success) {
          successCount++;
        } else {
          failedCount++;
        }
      }

      showStatus(
        `IK computed for ${successCount}/${waypointIndices.length} waypoints`,
        successCount > 0 ? 'success' : 'error'
      );

      // Re-visualize if visualizer is available
      if (visualizer && successCount > 0) {
        const waypointsWithWorld = currentWaypoints.map(wp => ({
          ...wp,
          // Add world coordinates for visualization
        }));
        visualizer.visualizeWaypoints(waypointsWithWorld);
      }

      return { success: successCount, failed: failedCount };
    },
    [ikSolver, computeIKForWaypoint, currentWaypoints, visualizer, showStatus]
  );

  /**
   * Verify current joint positions match waypoint pose
   * @param {number} waypointIndex - Index of waypoint to verify
   * @returns {Object} Verification result
   */
  const verifyWaypointIK = useCallback(
    (waypointIndex) => {
      if (!ikSolver) {
        return null;
      }

      const waypoint = currentWaypoints[waypointIndex];
      if (!waypoint || !waypoint.jointPositions) {
        return null;
      }

      const targetPose = {
        position: waypoint.position,
        rotation: waypoint.rotation,
      };

      // Use service to verify IK solution
      return verifyIKSolution(ikSolver, waypoint.jointPositions, targetPose);
    },
    [ikSolver, currentWaypoints]
  );

  /**
   * Get all IK solutions for a waypoint
   * @param {number} waypointIndex - Index of waypoint
   * @returns {Array<Array<number>>} Array of all valid IK solutions
   */
  const getAllIKSolutions = useCallback(
    (waypointIndex) => {
      if (!ikSolver) {
        return [];
      }

      const waypoint = currentWaypoints[waypointIndex];
      if (!waypoint) {
        return [];
      }

      const targetPose = {
        position: { ...waypoint.position },
        rotation: { ...waypoint.rotation },
      };

      // Use service to compute all solutions (already filtered and sorted)
      return computeAllIKSolutions(ikSolver, targetPose, waypoint.jointPositions);
    },
    [ikSolver, currentWaypoints]
  );

  /**
   * Apply a specific IK configuration to a waypoint
   * @param {number} waypointIndex - Index of waypoint to update
   * @param {number} configIndex - Index of the configuration to apply
   * @param {Array<Array<number>>} cachedSolutions - Pre-computed solutions (optional, will compute if not provided)
   * @returns {Array|null} Updated waypoints array if successful, null otherwise
   */
  const applyIKConfig = useCallback(
    async (waypointIndex, configIndex, cachedSolutions = null) => {
      if (!ikSolver) {
        showStatus('IK solver not available for this robot type', 'error');
        return null;
      }

      const waypoint = currentWaypoints[waypointIndex];
      if (!waypoint) {
        showStatus('Waypoint not found', 'error');
        return null;
      }

      try {
        // Use cached solutions if provided, otherwise compute
        const allSolutions = cachedSolutions || getAllIKSolutions(waypointIndex);
        
        if (configIndex < 0 || configIndex >= allSolutions.length) {
          showStatus(`Invalid config index: ${configIndex}`, 'error');
          return null;
        }

        const selectedConfig = allSolutions[configIndex];

        // Update waypoint with selected joint positions
        const updatedWaypoints = [...currentWaypoints];
        updatedWaypoints[waypointIndex] = {
          ...updatedWaypoints[waypointIndex],
          jointPositions: selectedConfig,
        };

        // Update parser
        parser.updateWaypointJoints(waypointIndex, selectedConfig);

        // Update state
        setCurrentWaypoints(updatedWaypoints);

        showStatus(`Applied config ${configIndex + 1} to waypoint ${waypoint.number}`, 'success');
        return updatedWaypoints;
      } catch (error) {
        console.error('Error applying IK config:', error);
        showStatus(`Error applying config: ${error.message}`, 'error');
        return null;
      }
    },
    [ikSolver, currentWaypoints, setCurrentWaypoints, parser, showStatus, getAllIKSolutions]
  );

  /**
   * Get robot information
   */
  const robotInfo = useMemo(() => {
    return ikSolver ? ikSolver.getRobotInfo() : null;
  }, [ikSolver]);

  return {
    ikSolver,
    robotInfo,
    computeIKForWaypoint,
    computeIKForMultipleWaypoints,
    verifyWaypointIK,
    getAllIKSolutions,
    applyIKConfig,
  };
}
